import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateMaterialTitleBar, DeleteMaterialModal, WarningMaterialModal } from "../../../../configs/material";
import TitleRow, { TitleAction } from "../../../../shared/components/TitleRow";
import CancelModal from "../../../../shared/components/CancelModal";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { ROLE } from "../../../../data/enums";
import { Material } from "../../../../data/entities";

interface TitleProps {
    setValidated: any;
    submitForm: any;
    title: string;
    data?: Material;
    onDataChange?: () => void;
    hasUnsavedChanges?: boolean;
    onCancelClick?: () => void;
    isLoading?: boolean;
    deleteMaterial?: () => Promise<void>;
    isDeleting?: boolean;
}

const Title = ({
                   setValidated,
                   submitForm,
                   data,
                   title,
                   onDataChange,
                   hasUnsavedChanges,
                   onCancelClick,
                   isLoading,
                   deleteMaterial,
                   isDeleting,
               }: TitleProps) => {
    const navigate = useNavigate();
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const handleCancel = () => {
        if (onDataChange) {
            onDataChange();
        }

        if (onCancelClick && hasUnsavedChanges) {
            onCancelClick();
        } else {
            setShowCancelModal(true);
        }
    };

    const handleConfirmCancel = () => {
        setShowCancelModal(false);
        navigate("/materials");
    };

    const handleSubmit = async () => {
        setValidated(true);
        await submitForm();
    };

    const baseTitleActions: TitleAction[] = [
        {
            title: CreateMaterialTitleBar.cancelButton,
            variant: "outlined",
            onClick: () => handleCancel(),
        },
        {
            title: CreateMaterialTitleBar.duplicateButton,
            variant: "outlined",
            onClick: () => {
                navigate(`/materials/create/`, {
                    state: { duplicateData: data },
                });
            },
        },
        {
            title: CreateMaterialTitleBar.addButton,
            variant: "contained",
            onClick: handleSubmit,
            isLoading: isLoading,
        },
    ];
    const titleActions = deleteMaterial && role === ROLE.MANAGER
        ? [
            {
                title: CreateMaterialTitleBar.deleteButton,
                variant: "outlined" as const,
                onClick: () => setShowDeleteModal(true),
                isLoading: isDeleting,
            },
            ...baseTitleActions,
        ]
        : baseTitleActions;

    const getTitleActions = () => {
        switch (role) {
            case ROLE.DELIVERER:
            case ROLE.SALE_MANAGER:
            case ROLE.SALE_STAFF:
                return [];
            case ROLE.STORAGE_KEEPER:
            case ROLE.FIRST_STORAGE_KEEPER:
            case ROLE.SECOND_STORAGE_KEEPER:
            case ROLE.LOGISTIC_MANAGER:
            case ROLE.LOGISTIC_STAFF:
            case ROLE.MANAGER:
            case ROLE.PURCHASING_STAFF:
            case ROLE.PRODUCTION_STAFF:
            case ROLE.ACCOUNTANT_MANAGER:
            case ROLE.FACTORY_STAFF:
            case ROLE.FACTORY_MANAGER_ONE:
            case ROLE.FACTORY_MANAGER_TWO:
            case ROLE.DEPUTY_FACTORY_MANAGER_ONE:
            case ROLE.DEPUTY_FACTORY_MANAGER_TWO:
                return titleActions;
            default:
                return titleActions;
        }
    };

    return (
        <>
            <TitleRow title={title} actions={getTitleActions()} />
            {showCancelModal && (
                <CancelModal
                    title={WarningMaterialModal.title}
                    contentText={WarningMaterialModal.context}
                    open={showCancelModal}
                    handleClose={() => setShowCancelModal(false)}
                    handleConfirm={handleConfirmCancel}
                />
            )}
            {showDeleteModal && deleteMaterial && (
                <CancelModal
                    title={DeleteMaterialModal.title}
                    contentText={DeleteMaterialModal.context}
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    handleConfirm={async () => {
                        await deleteMaterial();
                        setShowDeleteModal(false);
                    }}
                    confirmButtonText={isDeleting ? "Đang xoá..." : "Xoá"}
                />
            )}
        </>
    );
};

export default Title;
